import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Psychology as AIIcon,
  CloudQueue as BigDataIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  DataObject as DataIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DataAnalyticsCapability: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Data & Analytics Capability
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Advanced Analytics, Data Management, Business Intelligence & Machine Learning
          </Typography>
        </Box>
      </Box>

      {/* Capability Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Capability Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group delivers comprehensive data and analytics services that transform raw data into actionable 
            intelligence for mission-critical government operations. Our expertise spans the entire data lifecycle—from 
            ingestion and management through advanced analytics, machine learning, and visualization. We support CBP's 
            Advanced Trade Analytics Program (ATAP) processing billions of trade transactions, develop predictive models 
            for risk assessment, and create executive dashboards that drive data-driven decision making across DHS agencies.
          </Typography>
        </CardContent>
      </Card>

      {/* Advanced Analytics */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Advanced Analytics Services
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  ATAP Platform Enhancement
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced Trade Analytics Program for CBP trade intelligence.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Pattern Analysis:</strong> Identifying anomalies and trends</Typography></li>
                <li><Typography variant="body2"><strong>Risk Scoring:</strong> Automated targeting and screening</Typography></li>
                <li><Typography variant="body2"><strong>Network Analysis:</strong> Supply chain relationship mapping</Typography></li>
                <li><Typography variant="body2"><strong>Predictive Analytics:</strong> Forecasting trade volumes and risks</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Analytics:</strong> Live data processing and alerts</Typography></li>
                <li><Typography variant="body2"><strong>Big Data Processing:</strong> Billions of transactions analyzed</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Predictive Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Forecasting and predictive modeling for operational planning.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Time Series Forecasting:</strong> Volume and trend predictions</Typography></li>
                <li><Typography variant="body2"><strong>Regression Analysis:</strong> Statistical modeling and correlation</Typography></li>
                <li><Typography variant="body2"><strong>Classification Models:</strong> Risk categorization and scoring</Typography></li>
                <li><Typography variant="body2"><strong>Clustering Analysis:</strong> Pattern discovery and segmentation</Typography></li>
                <li><Typography variant="body2"><strong>Anomaly Detection:</strong> Outlier identification and alerting</Typography></li>
                <li><Typography variant="body2"><strong>What-If Analysis:</strong> Scenario modeling and simulation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DataIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Statistical Analysis
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced statistical methods for data-driven insights.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Descriptive Statistics:</strong> Mean, median, mode, distribution</Typography></li>
                <li><Typography variant="body2"><strong>Inferential Statistics:</strong> Hypothesis testing, confidence intervals</Typography></li>
                <li><Typography variant="body2"><strong>Correlation Analysis:</strong> Relationship identification</Typography></li>
                <li><Typography variant="body2"><strong>Multivariate Analysis:</strong> Multiple variable relationships</Typography></li>
                <li><Typography variant="body2"><strong>A/B Testing:</strong> Experimental design and analysis</Typography></li>
                <li><Typography variant="body2"><strong>Statistical Modeling:</strong> R, Python, SAS, SPSS</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Operational Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time operational intelligence and performance monitoring.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Performance Metrics:</strong> KPI tracking and monitoring</Typography></li>
                <li><Typography variant="body2"><strong>Process Analytics:</strong> Workflow efficiency analysis</Typography></li>
                <li><Typography variant="body2"><strong>Capacity Planning:</strong> Resource utilization forecasting</Typography></li>
                <li><Typography variant="body2"><strong>Quality Analytics:</strong> Defect analysis and improvement</Typography></li>
                <li><Typography variant="body2"><strong>Cost Analytics:</strong> Financial performance tracking</Typography></li>
                <li><Typography variant="body2"><strong>SLA Monitoring:</strong> Service level compliance tracking</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Management */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Management & Engineering
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                ETL & Data Integration
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Extract, Transform, Load (ETL) pipeline development</Typography></li>
                <li><Typography variant="body2">Data integration from multiple sources and systems</Typography></li>
                <li><Typography variant="body2">Real-time data streaming (Kafka, Kinesis)</Typography></li>
                <li><Typography variant="body2">Batch processing and scheduled data loads</Typography></li>
                <li><Typography variant="body2">Data quality validation and cleansing</Typography></li>
                <li><Typography variant="body2">ETL tools: Informatica, Talend, AWS Glue, Azure Data Factory</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Data Warehousing
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Enterprise data warehouse design and implementation</Typography></li>
                <li><Typography variant="body2">Dimensional modeling (star schema, snowflake schema)</Typography></li>
                <li><Typography variant="body2">Data mart development for specific business units</Typography></li>
                <li><Typography variant="body2">Slowly changing dimensions (SCD) management</Typography></li>
                <li><Typography variant="body2">Data warehouse platforms: Snowflake, Redshift, Synapse</Typography></li>
                <li><Typography variant="body2">Performance optimization and query tuning</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                Data Governance
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Data governance framework and policies</Typography></li>
                <li><Typography variant="body2">Data quality management and monitoring</Typography></li>
                <li><Typography variant="body2">Master data management (MDM)</Typography></li>
                <li><Typography variant="body2">Data lineage and impact analysis</Typography></li>
                <li><Typography variant="body2">Metadata management and data catalogs</Typography></li>
                <li><Typography variant="body2">Data stewardship and ownership models</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>
                Data Architecture
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Enterprise data architecture design</Typography></li>
                <li><Typography variant="body2">Data lake architecture (S3, ADLS, GCS)</Typography></li>
                <li><Typography variant="body2">Lambda and Kappa architectures for big data</Typography></li>
                <li><Typography variant="body2">Data mesh and domain-driven design</Typography></li>
                <li><Typography variant="body2">Data modeling and schema design</Typography></li>
                <li><Typography variant="body2">Cloud data platform architecture</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business Intelligence */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Business Intelligence & Visualization
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>BI Tool</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Capabilities</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Use Cases</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Tableau</strong></TableCell>
              <TableCell>Interactive dashboards, visual analytics, data storytelling</TableCell>
              <TableCell>Executive dashboards, operational reporting</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Power BI</strong></TableCell>
              <TableCell>Microsoft ecosystem integration, self-service BI, mobile</TableCell>
              <TableCell>Department reporting, KPI tracking</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Qlik Sense</strong></TableCell>
              <TableCell>Associative analytics, data discovery, collaboration</TableCell>
              <TableCell>Ad-hoc analysis, data exploration</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Looker</strong></TableCell>
              <TableCell>Data modeling, embedded analytics, API-first</TableCell>
              <TableCell>Application-embedded analytics</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>D3.js / Custom</strong></TableCell>
              <TableCell>Custom visualizations, web-based, interactive</TableCell>
              <TableCell>Specialized government dashboards</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Machine Learning & AI */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Machine Learning & Artificial Intelligence
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Machine Learning Models
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Supervised and unsupervised learning for government applications.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Classification:</strong> Risk scoring, fraud detection, categorization</Typography></li>
                <li><Typography variant="body2"><strong>Regression:</strong> Forecasting, trend analysis, prediction</Typography></li>
                <li><Typography variant="body2"><strong>Clustering:</strong> Segmentation, pattern discovery</Typography></li>
                <li><Typography variant="body2"><strong>Recommendation:</strong> Intelligent suggestions and routing</Typography></li>
                <li><Typography variant="body2"><strong>Time Series:</strong> Temporal pattern analysis and forecasting</Typography></li>
                <li><Typography variant="body2"><strong>Ensemble Methods:</strong> Random forests, gradient boosting</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Deep Learning & Neural Networks
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced AI for complex pattern recognition.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Computer Vision:</strong> Image recognition, object detection</Typography></li>
                <li><Typography variant="body2"><strong>Natural Language Processing:</strong> Text analysis, sentiment</Typography></li>
                <li><Typography variant="body2"><strong>Neural Networks:</strong> CNN, RNN, LSTM architectures</Typography></li>
                <li><Typography variant="body2"><strong>Transfer Learning:</strong> Pre-trained model adaptation</Typography></li>
                <li><Typography variant="body2"><strong>Deep Learning Frameworks:</strong> TensorFlow, PyTorch, Keras</Typography></li>
                <li><Typography variant="body2"><strong>Model Optimization:</strong> Hyperparameter tuning, pruning</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  MLOps & Model Management
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Production ML lifecycle management and operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Model Training:</strong> Automated training pipelines</Typography></li>
                <li><Typography variant="body2"><strong>Model Deployment:</strong> Production deployment and serving</Typography></li>
                <li><Typography variant="body2"><strong>Model Monitoring:</strong> Performance tracking and drift detection</Typography></li>
                <li><Typography variant="body2"><strong>Model Versioning:</strong> Version control and rollback</Typography></li>
                <li><Typography variant="body2"><strong>A/B Testing:</strong> Model comparison and validation</Typography></li>
                <li><Typography variant="body2"><strong>MLOps Platforms:</strong> MLflow, Kubeflow, SageMaker</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  AI Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Embedding AI capabilities into government applications.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>API Integration:</strong> RESTful ML model endpoints</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Inference:</strong> Low-latency prediction serving</Typography></li>
                <li><Typography variant="body2"><strong>Batch Prediction:</strong> Large-scale scoring jobs</Typography></li>
                <li><Typography variant="body2"><strong>Edge AI:</strong> On-device model deployment</Typography></li>
                <li><Typography variant="body2"><strong>AutoML:</strong> Automated model selection and tuning</Typography></li>
                <li><Typography variant="body2"><strong>Explainable AI:</strong> Model interpretability and transparency</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Big Data Technologies */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BigDataIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Big Data Technologies & Platforms
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Hadoop Ecosystem
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">HDFS distributed file system</Typography></li>
                <li><Typography variant="body2">MapReduce batch processing</Typography></li>
                <li><Typography variant="body2">YARN resource management</Typography></li>
                <li><Typography variant="body2">Hive data warehousing</Typography></li>
                <li><Typography variant="body2">HBase NoSQL database</Typography></li>
                <li><Typography variant="body2">Pig data flow scripting</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                Apache Spark
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">In-memory data processing</Typography></li>
                <li><Typography variant="body2">Spark SQL for structured data</Typography></li>
                <li><Typography variant="body2">Spark Streaming for real-time</Typography></li>
                <li><Typography variant="body2">MLlib machine learning library</Typography></li>
                <li><Typography variant="body2">GraphX graph processing</Typography></li>
                <li><Typography variant="body2">PySpark, Scala, Java APIs</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Data Lake Architecture
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">AWS S3 data lake storage</Typography></li>
                <li><Typography variant="body2">Azure Data Lake Storage</Typography></li>
                <li><Typography variant="body2">Delta Lake ACID transactions</Typography></li>
                <li><Typography variant="body2">Data lake governance and security</Typography></li>
                <li><Typography variant="body2">Schema-on-read flexibility</Typography></li>
                <li><Typography variant="body2">Multi-format support (Parquet, ORC)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Applications */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Government Analytics Applications
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Trade & Cargo Analytics:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">ATAP trade pattern analysis and risk scoring</Typography></li>
                <li><Typography variant="body2">Cargo volume forecasting and capacity planning</Typography></li>
                <li><Typography variant="body2">Trade compliance analytics and enforcement</Typography></li>
                <li><Typography variant="body2">Supply chain network analysis and visualization</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Identity & Security Analytics:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Biometric matching and identity verification analytics</Typography></li>
                <li><Typography variant="body2">Fraud detection and anomaly identification</Typography></li>
                <li><Typography variant="body2">Student visa compliance tracking and analytics</Typography></li>
                <li><Typography variant="body2">Security threat assessment and risk modeling</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Analytics Performance Metrics
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                Billions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Transactions Analyzed
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ATAP trade data
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                95%+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Model Accuracy
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ML prediction accuracy
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                Real-Time
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analytics Processing
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Live data insights
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                Petabytes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data Processed
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Big data scale
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataAnalyticsCapability;
